import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, type FC, type PropsWithChildren } from "react";
import { debounce } from "throttle-debounce";
import { AppForm } from "@/shared/Components";
import { useUpdatePersonMutation, type Person } from "@/services/api";
import { useAppDispatch } from "@/services/store";
import { useAlert } from "@/shared/hooks";
import { getErrorMessage } from "@/services/errors";
import {
  setFormIsValid,
  setCurrentPerson,
  setFormIsValidating,
  setUpdateIsLoading,
} from "@/pages/ContactsPage/slice";
import type { ActivitiesTabFormValues } from "../../schemas";
import {
  getDefaultActivitiesTabFormValues,
  transformActivitiesFormDataToUpdatePerson,
} from "../../helpers";
import { getActivitiesTabSchema } from "../../schemas";

type Props = {
  defaultValuesPerson: Person;
};

export const ActivitiesTabForm: FC<PropsWithChildren<Props>> = ({
  children,
  defaultValuesPerson,
}) => {
  const dispatch = useAppDispatch();

  const [updatePerson] = useUpdatePersonMutation();

  const { callError } = useAlert();

  const defaultValues = useMemo(() => {
    return getDefaultActivitiesTabFormValues(defaultValuesPerson);
  }, [defaultValuesPerson]);

  const formApi = useForm({
    resolver: yupResolver(getActivitiesTabSchema()),
    mode: "all",
    values: defaultValues,
    shouldFocusError: false,
  });

  const onSubmit = useCallback(
    async (data: ActivitiesTabFormValues) => {
      const transformedData = transformActivitiesFormDataToUpdatePerson({
        data,
        defaultValuesPerson,
      });

      const res = await updatePerson(transformedData)
        .unwrap()
        .catch((e) => {
          callError({ text: getErrorMessage(e.code) });
        });

      if (res) {
        dispatch(setUpdateIsLoading(false));
        dispatch(setCurrentPerson(res));
      } else {
        dispatch(setUpdateIsLoading(false));
      }
    },
    [updatePerson, dispatch, defaultValuesPerson],
  );

  const submit = useMemo(
    () =>
      debounce(4000, async (data: ActivitiesTabFormValues) => {
        const isValid = await formApi.trigger();
        if (isValid) {
          onSubmit(data);
        }
      }),
    [formApi, onSubmit],
  );

  const triggerHandler = () => {
    dispatch(setFormIsValidating(true));

    return formApi.trigger().then((valid) => {
      dispatch(setFormIsValidating(false));
      dispatch(setFormIsValid(valid));
      return valid;
    });
  };

  useEffect(() => {
    triggerHandler();
  }, []);

  useEffect(() => {
    const subscription = formApi.watch(async (values) => {
      const isValid = await triggerHandler();

      if (isValid && formApi.formState.isDirty) {
        dispatch(setUpdateIsLoading(true));

        submit(values as ActivitiesTabFormValues);
      }

      if (!isValid) {
        dispatch(setUpdateIsLoading(false));
      }
    });

    return () => subscription.unsubscribe();
  }, [formApi.watch, submit]);

  return <AppForm formApi={formApi}>{children}</AppForm>;
};
